import React from 'react';

import { Callout, Text } from '../../../../components';
import type { MailboxError } from '../../../../mock-data/mailbox-records';
import { RetryLink } from './LoadFailedNotice.styles';

export type LoadFailedNoticeProps = {
  /** Why the mailboxes could not be read: the service's error, or `null` when it did not answer. */
  error: MailboxError | null;
  /** Re-run the read the page already does. Offered only when the service did not answer: every
   *  other case here does not clear by asking again. */
  onRetry?: () => void;
};

/**
 * What the section and the mailbox page say when the brand's mailboxes could not be read.
 *  - `unauthorized` (401, or no token at all): the owner's SESSION failed, not the mailbox section,
 *    so it says to sign in again. Nothing retries it.
 *  - `forbidden` (403): the domain is not this owner's. The service's own words, verbatim, and no line
 *    of ours guessing why: a 403 deliberately does not tell someone else's domain from one that does
 *    not exist. A bodiless one (a gateway's) gets a plain "not available" line, never "try again":
 *    a 403 does not clear by waiting.
 *  - anything else the service answered: its own words.
 *  - the service DID NOT ANSWER (a network failure, a gateway's 502, an HTML error page: `error` is
 *    `null`): the service cannot be reached RIGHT NOW, a transient failure, so it offers Try again.
 *    Worded apart from the section's "not available in this environment" (no service configured
 *    at all), which nothing retries: collapsing the two makes an operator retry what never works,
 *    or give up on what would.
 */
export const LoadFailedNotice = ({ error, onRetry }: LoadFailedNoticeProps): React.ReactElement =>
  error?.code === 'unauthorized' ? (
    <Callout tone="danger" text="Your session has ended, so sign in again to see your mailboxes." />
  ) : error?.code === 'forbidden' && error.message ? (
    <Callout tone="danger" text={error.message} />
  ) : error?.code === 'forbidden' ? (
    <Callout tone="danger" text="Mail on this domain is not available to your account." />
  ) : error ? (
    <Callout tone="danger" title="Mailboxes could not be loaded" text={error.message} />
  ) : (
    <div data-testid="mailbox-unreachable-notice">
      <Callout
        tone="danger"
        text="The mailbox service cannot be reached right now."
        action={
          onRetry ? (
            <RetryLink type="button" onClick={onRetry} data-testid="mailbox-unreachable-retry">
              <Text text="Try again" tag="span" />
            </RetryLink>
          ) : undefined
        }
      />
    </div>
  );

export default LoadFailedNotice;
